/**
 * Org membership + invitations API — typed client for user-service's
 * collaboration endpoints (gateway prefix /api/v1/users, already routed).
 *
 * Mirrors docs/COLLABORATION_CONTRACT.md §1 exactly — every field/route here is
 * FROZEN. `org_role` is a NEW, narrower vocabulary (owner|admin|member) than the
 * pre-existing tenant member directory's `MemberRole` (src/lib/api/members.ts,
 * `GET /users/members`): that endpoint lists the Keycloak-backed directory used
 * by pickers (OwnerPicker, share grantee picker); THIS module is the
 * org-membership/invite lifecycle (who is in the org, at what org_role, invited
 * vs active) that gates collaboration writes (invite, share, assign).
 *
 * Access model (contract §0): owner/admin see all tenant data unconditionally;
 * `member` is scoped via share grants (see lib/api/crm.ts `CrmShare`). A solo
 * user is `owner` and notices nothing different.
 */

import { api } from '@/lib/api/client';
import { tr } from '@/i18n';

const BASE = '/api/v1/users/org';

export type OrgRole = 'owner' | 'admin' | 'member';
export type OrgMemberStatus = 'invited' | 'active' | 'suspended';

export const ORG_ROLES: OrgRole[] = ['owner', 'admin', 'member'];

export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  owner: tr('shell.lib.apiOrg.owner'),
  admin: tr('shell.lib.apiOrg.admin'),
  member: tr('shell.lib.apiOrg.member'),
};

export const ORG_STATUS_LABELS: Record<OrgMemberStatus, string> = {
  invited: tr('shell.lib.apiOrg.guest'),
  active: tr('shell.lib.apiOrg.active'),
  suspended: tr('shell.lib.apiOrg.suspended'),
};

/** `org_members` row (contract §1) — `user_id` is null until the invite is accepted. */
export interface OrgMember {
  id: string;
  tenant_id: string;
  user_id: string | null;
  email: string;
  org_role: OrgRole;
  status: OrgMemberStatus;
  invited_by: string | null;
  invite_expires_at: string | null;
  created_at: string;
  joined_at: string | null;
}

export interface OrgInvitationCreate {
  email: string;
  org_role: OrgRole;
}

/** Response of `POST /org/invitations` — the accept link is pre-built by the
 *  server (`{FRONTEND_URL}/accept-org-invite/{token}`). */
export interface OrgInvitationResult {
  invite_token: string;
  accept_url: string;
}

export interface OrgMemberUpdate {
  org_role?: OrgRole;
  status?: OrgMemberStatus;
}

/** Best-effort ack for a membership removal — tolerated loosely since the
 *  contract doesn't pin an exact envelope; callers should refetch the list
 *  regardless of the shape returned. */
export interface OrgMemberRemoveResult {
  user_id?: string;
  status?: string;
}

/** `POST /org/invitations/{token}/accept` result. The invitee flow (contract
 *  §1) may write the membership under the INVITING tenant when it differs from
 *  the caller's current JWT tenant — `tenant_id` here lets the SPA detect that
 *  and prompt a re-login so a fresh token carries the right tenant. */
export interface OrgInviteAcceptResult {
  status: string; // "attached"
  tenant_id: string;
  user_id: string;
  org_role: OrgRole;
  provisioned_account: boolean;
  /** True when the membership was written under the INVITING tenant, which
   *  differs from the caller's current JWT tenant — the SPA must prompt a
   *  sign-out/in so a fresh token carries the right tenant. */
  reissue_required: boolean;
  message: string | null;
}

/** List the caller's org membership roster (read is open to any tenant member;
 *  writes below are admin+, enforced server-side). */
export function listOrgMembers(): Promise<OrgMember[]> {
  return api.get<OrgMember[]>(`${BASE}/members`);
}

/** Admin-only: invite a new/returning member. RBAC rule (frozen): granting
 *  `org_role=owner` is rejected server-side unless the caller is an owner. */
export function createOrgInvitation(data: OrgInvitationCreate): Promise<OrgInvitationResult> {
  return api.post<OrgInvitationResult>(`${BASE}/invitations`, data);
}

/** Public, non-consuming preview of an invitation for the accept landing —
 *  who invited whom to which org, and whether the link is still usable. No auth
 *  required; never throws on a bad token (returns `valid:false` + a reason). */
export interface OrgInvitationInfo {
  email: string;
  tenant_id: string;
  org_role: OrgRole;
  valid: boolean;
  reason: string | null;
  already_member: boolean;
  expired: boolean;
}

export function getOrgInvitationInfo(token: string): Promise<OrgInvitationInfo> {
  return api.get<OrgInvitationInfo>(`${BASE}/invitations/${encodeURIComponent(token)}`);
}

/** Redeem an invite token. The invitee must be signed in (the accept landing
 *  routes account-less visitors through Keycloak sign-in/sign-up first, then
 *  returns here authenticated); the server then attaches their identity to the
 *  membership. Works for brand-new AND already-registered users. */
export function acceptOrgInvitation(token: string): Promise<OrgInviteAcceptResult> {
  return api.post<OrgInviteAcceptResult>(
    `${BASE}/invitations/${encodeURIComponent(token)}/accept`,
  );
}

/** Admin-only role/status change. Cannot set `org_role=owner` unless the
 *  caller is an owner (frozen RBAC rule) — the server enforces this; a 403
 *  surfaces to the caller. */
export function updateOrgMember(userId: string, data: OrgMemberUpdate): Promise<OrgMember> {
  return api.patch<OrgMember>(`${BASE}/members/${encodeURIComponent(userId)}`, data);
}

/** Admin-only removal. The server refuses to remove the last owner (409). */
export function removeOrgMember(userId: string): Promise<OrgMemberRemoveResult> {
  return api.delete<OrgMemberRemoveResult>(`${BASE}/members/${encodeURIComponent(userId)}`);
}

/** Admin-only revoke of a PENDING invitation (by its roster-row id — user_id is
 *  null before acceptance, so removeOrgMember can't cancel it). */
export function revokeOrgInvitation(memberId: string): Promise<void> {
  return api.delete<void>(`${BASE}/invitations/${encodeURIComponent(memberId)}`);
}
